// SKYE WIKI — Sophia's own offline encyclopedia.
// Article text and pictures come from Wikipedia (CC BY-SA, credited on every
// page) and are stored on this device in IndexedDB so they open with no signal.
// Wikimedia's APIs allow cross-origin reads, so the browser fetches directly.

import { openDB, type IDBPDatabase } from "idb";
import { withRetry } from "@/lib/sophia/callGuard";

export interface WikiArticle { title: string; html: string; text: string; pack: string; savedAt: number; bytes: number; images: string[] }
interface ImageRow { url: string; blob: Blob; bytes: number }

let dbp: Promise<IDBPDatabase> | null = null;
export function wikiDb() {
  if (!dbp) {
    // Ask the browser to keep saved pages permanently instead of clearing them when space runs low.
    try { void navigator.storage?.persist?.(); } catch { /* unsupported */ }
    dbp = openDB("sophia-skyewiki", 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("articles")) {
          const s = d.createObjectStore("articles", { keyPath: "title" });
          s.createIndex("pack", "pack");
        }
        if (!d.objectStoreNames.contains("images")) d.createObjectStore("images", { keyPath: "url" });
        if (!d.objectStoreNames.contains("sites")) d.createObjectStore("sites", { keyPath: "id" });
        if (!d.objectStoreNames.contains("assets")) d.createObjectStore("assets", { keyPath: "url" });
      },
    });
  }
  return dbp;
}

const WP = "https://en.wikipedia.org";
export const normTitle = (t: string) => decodeURIComponent(t).replace(/_/g, " ").trim();
const slug = (t: string) => encodeURIComponent(t.replace(/ /g, "_"));

async function getJson<T>(url: string): Promise<T> {
  return withRetry(async () => {
    const r = await fetch(url);
    if (!r.ok) throw Object.assign(new Error(`HTTP ${r.status}`), { status: r.status });
    return (await r.json()) as T;
  });
}

function absUrl(u: string) { return u.startsWith("//") ? "https:" + u : u; }

async function saveImage(url: string): Promise<number> {
  const d = await wikiDb();
  if (await d.get("images", url)) return 0;
  try {
    const r = await fetch(url);
    if (!r.ok) return 0;
    const blob = await r.blob();
    await d.put("images", { url, blob, bytes: blob.size } as ImageRow);
    return blob.size;
  } catch { return 0; }
}

/** Download one article with its pictures and store it. */
export async function saveArticle(title: string, pack = "browsed", withImages = true): Promise<WikiArticle> {
  const d = await wikiDb();
  const t = normTitle(title);
  const existing = (await d.get("articles", t)) as WikiArticle | undefined;
  if (existing) return existing;
  const html = await withRetry(async () => {
    const r = await fetch(`${WP}/api/rest_v1/page/html/${slug(t)}?redirect=true`);
    if (!r.ok) throw Object.assign(new Error(r.status === 404 ? "That article doesn't exist." : `HTTP ${r.status}`), { status: r.status });
    return r.text();
  });
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, link, meta, base").forEach((n) => n.remove());
  const images: string[] = [];
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src");
    img.removeAttribute("srcset");
    if (!src) return;
    const u = absUrl(src);
    img.setAttribute("src", u);
    img.setAttribute("loading", "lazy");
    images.push(u);
  });
  const body = doc.body.innerHTML;
  const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  let bytes = body.length;
  if (withImages) {
    for (const u of images.slice(0, 40)) bytes += await saveImage(u);
  }
  const art: WikiArticle = { title: t, html: body, text, pack, savedAt: Date.now(), bytes, images };
  await d.put("articles", art);
  return art;
}

export async function getArticle(title: string): Promise<WikiArticle | undefined> {
  return (await wikiDb()).get("articles", normTitle(title));
}

/** Rewrites stored picture links to local copies and article links to Skye-Net. */
export async function renderArticle(art: WikiArticle): Promise<{ html: string; urls: string[] }> {
  const d = await wikiDb();
  const doc = new DOMParser().parseFromString(art.html, "text/html");
  const urls: string[] = [];
  for (const img of Array.from(doc.querySelectorAll("img"))) {
    const src = img.getAttribute("src") || "";
    const row = (await d.get("images", src)) as ImageRow | undefined;
    if (row) { const u = URL.createObjectURL(row.blob); urls.push(u); img.setAttribute("src", u); }
  }
  doc.querySelectorAll("a[href]").forEach((a) => {
    const h = a.getAttribute("href") || "";
    if (h.startsWith("./")) {
      const name = h.slice(2).split("#")[0];
      a.setAttribute("href", `/skye-wiki/${name}`);
      a.setAttribute("data-wiki", normTitle(name));
    } else {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    }
  });
  return { html: doc.body.innerHTML, urls };
}

export async function listArticles(): Promise<WikiArticle[]> {
  return (await wikiDb()).getAll("articles");
}

export async function searchLocal(q: string, limit = 30): Promise<WikiArticle[]> {
  const needle = q.toLowerCase().trim();
  if (!needle) return [];
  const all = await listArticles();
  const scored = all.map((a) => {
    const t = a.title.toLowerCase();
    const s = t === needle ? 100 : t.startsWith(needle) ? 50 : t.includes(needle) ? 20 : a.text.toLowerCase().includes(needle) ? 5 : 0;
    return { a, s };
  }).filter((x) => x.s > 0).sort((x, y) => y.s - x.s);
  return scored.slice(0, limit).map((x) => x.a);
}

export async function searchOnline(q: string): Promise<string[]> {
  const j = await getJson<[string, string[]]>(`${WP}/w/api.php?action=opensearch&limit=12&format=json&origin=*&search=${encodeURIComponent(q)}`);
  return j[1] || [];
}

/** Short passage for Sophia's offline answers. */
export async function wikiAnswer(q: string): Promise<{ title: string; excerpt: string } | null> {
  const hit = (await searchLocal(q, 1))[0];
  if (!hit) return null;
  return { title: hit.title, excerpt: hit.text.slice(0, 600) };
}

export async function deletePack(pack: string) {
  const d = await wikiDb();
  const all = (await d.getAllFromIndex("articles", "pack", pack)) as WikiArticle[];
  const tx = d.transaction(["articles", "images"], "readwrite");
  for (const a of all) {
    await tx.objectStore("articles").delete(a.title);
    for (const u of a.images) await tx.objectStore("images").delete(u);
  }
  await tx.done;
}

// ---------- packs ----------
export const TOPIC_PACKS: { id: string; label: string; categories: string[] }[] = [
  { id: "science", label: "Science", categories: ["Physics", "Chemistry", "Biology", "Astronomy"] },
  { id: "history", label: "History", categories: ["History of England", "Ancient history", "World War II"] },
  { id: "lgbtq", label: "LGBTQ+", categories: ["LGBT history", "Transgender topics", "LGBT rights"] },
  { id: "law", label: "Law and rights", categories: ["English law", "Human rights", "Criminal law"] },
  { id: "health", label: "Health", categories: ["Mental health", "Diseases and disorders", "Nutrition"] },
  { id: "uk", label: "Staffordshire and the UK", categories: ["Staffordshire", "Stoke-on-Trent", "Geography of England"] },
  { id: "ancient", label: "Religion and ancient history", categories: ["Mesopotamian mythology", "Sumer", "Religion"] },
  { id: "tech", label: "Technology", categories: ["Computing", "Electronics", "Renewable energy"] },
];

async function topTitles(count: number): Promise<string[]> {
  const out = new Set<string>();
  const now = new Date();
  for (let m = 1; m <= 12 && out.size < count; m++) {
    const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
    const url = `https://wikimedia.org/api/rest_v1/metrics/pageviews/top/en.wikipedia/all-access/${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/all-days`;
    try {
      const j = await getJson<{ items: { articles: { article: string }[] }[] }>(url);
      for (const a of j.items[0]?.articles ?? []) {
        if (/^(Main_Page|Special:|Wikipedia:|File:|Portal:|Help:|Talk:)/.test(a.article) || a.article === "-") continue;
        out.add(normTitle(a.article));
        if (out.size >= count) break;
      }
    } catch { /* skip month */ }
  }
  return [...out];
}

async function categoryTitles(cat: string, max = 500): Promise<string[]> {
  const j = await getJson<{ query?: { categorymembers: { title: string }[] } }>(
    `${WP}/w/api.php?action=query&list=categorymembers&cmtype=page&cmlimit=${max}&format=json&origin=*&cmtitle=${encodeURIComponent("Category:" + cat)}`,
  );
  return (j.query?.categorymembers ?? []).map((m) => m.title);
}

export async function packTitles(pack: string, count = 5000): Promise<string[]> {
  if (pack === "starter") return topTitles(count);
  const p = TOPIC_PACKS.find((x) => x.id === pack);
  if (!p) return [];
  const all = new Set<string>();
  for (const c of p.categories) { try { (await categoryTitles(c)).forEach((t) => all.add(t)); } catch { /* ignore */ } }
  return [...all];
}

// ---------- resumable download queue ----------
interface Job { pack: string; titles: string[]; done: number; failed: number }
const JOB_KEY = "sophia:skyewiki:jobs";
type Listener = (jobs: Job[], running: boolean) => void;
const listeners = new Set<Listener>();
let running = false;
let stopFlag = false;

export function loadJobs(): Job[] {
  try { return JSON.parse(localStorage.getItem(JOB_KEY) || "[]"); } catch { return []; }
}
function saveJobs(j: Job[]) { try { localStorage.setItem(JOB_KEY, JSON.stringify(j)); } catch { /* full */ } }
function emit() { const j = loadJobs(); listeners.forEach((l) => l(j, running)); }
export function onJobs(l: Listener) { listeners.add(l); l(loadJobs(), running); return () => { listeners.delete(l); }; }

export async function queuePack(pack: string, count = 5000) {
  const titles = await packTitles(pack, count);
  const jobs = loadJobs().filter((j) => j.pack !== pack);
  const old = loadJobs().find((j) => j.pack === pack);
  jobs.push({ pack, titles, done: old && old.titles.length <= titles.length ? old.done : 0, failed: 0 });
  saveJobs(jobs);
  emit();
  void runQueue();
}

export function pauseQueue() { stopFlag = true; }
export function cancelJob(pack: string) { saveJobs(loadJobs().filter((j) => j.pack !== pack)); emit(); }

export async function runQueue() {
  if (running) return;
  running = true; stopFlag = false; emit();
  try {
    for (;;) {
      const jobs = loadJobs();
      const job = jobs.find((j) => j.done < j.titles.length);
      if (!job || stopFlag || !navigator.onLine) break;
      const batch = job.titles.slice(job.done, job.done + 4);
      const res = await Promise.allSettled(batch.map((t) => saveArticle(t, job.pack)));
      const fresh = loadJobs();
      const j2 = fresh.find((j) => j.pack === job.pack);
      if (!j2) continue;
      j2.done += batch.length;
      j2.failed += res.filter((r) => r.status === "rejected").length;
      saveJobs(fresh);
      emit();
    }
  } finally { running = false; emit(); }
}

export async function storageInfo() {
  let persisted = false;
  try { persisted = (await navigator.storage?.persisted?.()) ?? false; } catch { /* */ }
  const est = await navigator.storage?.estimate?.().catch(() => undefined);
  return { persisted, usage: est?.usage ?? 0, quota: est?.quota ?? 0 };
}
export async function askPersist() { try { return (await navigator.storage?.persist?.()) ?? false; } catch { return false; } }
export const fmtBytes = (n: number) => n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(0)} MB` : `${Math.round(n / 1e3)} KB`;

const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
/** Save an article you wrote yourself. Plain text only — blank lines make paragraphs, "## " makes a heading. */
export async function writeOwnArticle(title: string, body: string): Promise<WikiArticle> {
  const t = title.trim().slice(0, 120);
  if (!t || !body.trim()) throw new Error("Give the article a title and some words.");
  const html = body.trim().split(/\n\s*\n/).map((b) => b.startsWith("## ") ? `<h2>${esc(b.slice(3))}</h2>` : `<p>${esc(b).replace(/\n/g, "<br>")}</p>`).join("");
  const art: WikiArticle = { title: t, html, text: body.trim(), pack: "mine", savedAt: Date.now(), bytes: html.length * 2, images: [] };
  await (await wikiDb()).put("articles", art);
  return art;
}
